"use client";
import { useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from "@/lib/utils";
import SharedHero from '@/components/ui/shared-hero';
import Image from "next/image";
import dynamic from 'next/dynamic';

// Dynamic import for the features list
const FeaturesList = dynamic(() => import('./components/features-list'), {
    loading: () => <div className="h-96 w-full animate-pulse bg-gray-100 rounded-2xl" />,
    ssr: false // Optional: depending on SEO needs, usually features should be SSR'd for SEO but for pure speed we can lazy load specific parts. 
    // Actually, for SEO "Features" content is important. Let's keep SSR default (true) but splitting code helps bundle size.
    // If we want faster "load" interactions, we can leave SSR true.
});

// Product Feature Images (S3)
const presentationBuilderImg = "https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1769413061057_jigx42pur9_Presentation builder.png";
const fundraisingDeckImg = "https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1769413061015_1pg47be4hwi_Fundraising Deck.png";
const businessReportImg = "https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1769413061256_iuzae7frr7r_Business Report.png";
const captableImg = "https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1769413061086_8lrnqs2lka4_Captable.png";
const partnershipDeckImg = "https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1769413061291_2fx4fgcydty_Partnership Deck.png";
const teamIntroImg = "https://seedicon-dev-public-images.s3.ap-south-1.amazonaws.com/1769413061248_uz3s2by7zga_Team Introduction.png";


const products = [
    {
        id: "presentation-builder",
        title: "AI-generated presentations without the design headache",
        description: "Turn your ideas into polished presentations in minutes. Write a short brief, and Deckwale creates slide decks with clear flow, engaging layout, and visual hierarchy — no manual formatting needed.",
        image: presentationBuilderImg,
        label: "Presentation builder",
        link: "/products/presentation-builder"
    },
    {
        id: "pitch-deck",
        title: "Investor-ready pitch decks — fast",
        description: "Get a complete pitch deck specifically tailored for fundraising. Deckwale’s AI helps you frame your story, emphasize traction, and define your market opportunity so you can walk into investor meetings with confidence.",
        image: fundraisingDeckImg,
        label: "Fundraising pitch deck",
        link: "/products/fundraising-pitch-deck"
    },
    {
        id: "business-report",
        title: "Professional reports in minutes",
        description: "From quarterly results to operational summaries, Deckwale helps teams produce structured, data-ready business reports. Focus on insights — we handle sectioning, formatting, and clarity.",
        image: businessReportImg,
        label: "Business report generator",
        link: "/products/business-report"
    },

    {
        id: "captable",
        title: "Organized, clear cap tables for founders & finance teams",
        description: "Generate structured cap tables that show ownership, equity, and investor details at a glance. Ideal for funding rounds, due diligence, and stakeholder discussions.",
        image: captableImg,
        label: "Cap table structure tool",
        link: "/products/cap-table-structure"
    },
    {
        id: "partnership-proposal",
        title: "Professional partnership docs — fast",
        description: "Craft compelling proposals that help you form alliances, pitch collaborations, or negotiate agreements. AI structures your value proposition and benefits in a business-ready format.",
        image: partnershipDeckImg,
        label: "Partnership proposal builder",
        link: "/products/partnership-proposal-builder"
    },
    {
        id: "team-intro",
        title: "Showcase your team professionally",
        description: "Introduce your team with AI-generated bios, roles, and visual layouts. Ideal for pitch decks, websites, and investor decks where team credibility matters.",
        image: teamIntroImg,
        label: "Team introduction generator",
        link: "/products/team-introduction"
    },

];

const Products = () => {
    const [activeId, setActiveId] = useState(products[0].id);

    return (
        <div className="min-h-screen font-jakarta bg-white">
            <SharedHero
                label="Products"
                title="Deckwale Product Suites"
                subtitle="Each Deckwale product uses advanced AI to understand your input, convert it into structured content, and deliver professional output."
            />

            {/* Accordion Container */}
            <div className="mx-[80px] h-[700px] flex gap-2 -mt-12 relative z-20">
                {products.map((product) => (
                    <motion.div
                        key={product.id}
                        layout
                        onClick={() => setActiveId(product.id)}
                        className={cn(
                            "relative h-full rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ease-spring border",
                            activeId === product.id
                                ? "flex-grow-[3] border-[#2563EB]/20 shadow-xl shadow-blue-900/5 bg-[#F8FBFF]"
                                : "flex-grow-0 w-14 md:w-16 bg-[#EFF6FF] hover:bg-blue-100 border-transparent hover:border-blue-200"
                        )}
                        transition={{ type: "spring", stiffness: 200, damping: 25 }}
                    >
                        {/* Active Content */}
                        {activeId === product.id ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                                className="w-full h-full flex flex-col p-8 md:p-10"
                            >
                                <div className="max-w-3xl shrink-0">
                                    <motion.div
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3, delay: 0.2 }}
                                        className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-[#2563EB] uppercase bg-blue-100/50 rounded-full font-syne"
                                    >
                                        {product.label}
                                    </motion.div>
                                    <motion.h2
                                        layout="position"
                                        className="font-syne font-bold text-2xl md:text-3xl text-[#000000] mb-5 leading-none"
                                    >
                                        {product.title}
                                    </motion.h2>
                                    <motion.p
                                        initial={{ opacity: 0, y: 5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-gray-600 text-[14px] leading-relaxed max-w-3xl"
                                    >
                                        {product.description}
                                    </motion.p>

                                    {product.link && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.1 }}
                                            className="mt-6"
                                        >
                                            <a
                                                href={product.link}
                                                className="inline-flex items-center gap-2 text-[#2563EB] font-bold text-sm hover:underline underline-offset-4"
                                            >
                                                See more
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </a>
                                        </motion.div>
                                    )}
                                </div>

                                {/* Stock Image Visualization - Fills Remaining Space */}
                                <div className="w-full flex-1 rounded-xl overflow-hidden shadow-inner border border-blue-100 mt-6 relative">
                                    <Image
                                        src={product.image}
                                        alt={product.title}
                                        fill
                                        className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        sizes="(max-width: 768px) 100vw, 80vw"
                                        priority={true} // Priority loading for active product
                                    />
                                </div>
                            </motion.div>
                        ) : (
                            /* Inactive Vertical Label */
                            <div className="h-full w-full flex items-center justify-center">
                                <div className="-rotate-90 transform whitespace-nowrap origin-center flex items-center gap-2">
                                    <span className="text-[#93C5FD] font-syne font-semibold text-xl group-hover:text-[#2563EB] transition-colors">
                                        {product.label}
                                    </span>
                                </div>
                            </div>
                        )}
                    </motion.div>
                ))}
            </div>

            {/* Features Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-32 mb-24">

                {/* Section Header */}
                <div className="text-center mb-24">
                    <div className="text-center">
                        <h2 className="font-syne font-bold text-3xl md:text-4xl tracking-tight mb-4">
                            Features
                        </h2>
                        <div className="h-1 w-20 bg-blue-600 mx-auto rounded-full" />
                    </div>
                </div>

                {/* Features List Component (Dynamically Loaded) */}
                <FeaturesList />
            </div>
        </div>
    );
}

export default Products;
